import { useEffect, useState, useRef } from "react";
import {
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Divider,
  Grid,
} from "@mui/material";
import { useParams } from "react-router-dom";
import QRCode from "qrcode.react";
import ReactToPrint from "react-to-print";
import { FetchedData } from "./ContractorFormPage";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import BackButton from "../../atoms/Button/BrowserBackButton";
import haudiLogo from "../../../assets/images/haudiLogo.png";
import fulltechLogo from "../../../assets/images/fulltechLogo.png";
import iconFtSmartkey from "../../../assets/images/icon_ftsmartkey.png";
import iconFamiliaCall from "../../../assets/images/icon_familiacall.png";

interface QrKeyData {
  vendor: string;
  version: string;
  id: string;
  password: string;
  eep?: string;
}

interface QrKeyDataWithId extends QrKeyData {
  autoKeyId: number;
  keyIndexInContract: number;
  doorPlace: string;
}

const MasterKeyQrPage = () => {
  const { autoBuildingId, autoContractId, autoKeyId } = useParams<{
    autoBuildingId: string;
    autoContractId: string;
    autoKeyId?: string;
  }>();
  const qrCodesRef = useRef<HTMLDivElement>(null);

  const [contractData, setContractData] = useState<FetchedData | undefined>(undefined);
  console.log("URLパラメータautoKeyId：" + autoKeyId);

  const [keys, setKeys] = useState<QrKeyDataWithId[]>([]);

  const currentDateTime = new Date().toLocaleString("ja-JP", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  useEffect(() => {
    // 内部の非同期関数を定義
    const fetchData = async () => {
      // JWTトークンを取得します
      const jwtToken = await getJwtTokenFromIdToken();

      try {
        // call showContract API
        const response = await fetch(
          process.env.REACT_APP_API_ENDPOINT +
          `/buildings/${autoBuildingId}/contracts/${autoContractId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: jwtToken,
            },
          },
        );

        const data: FetchedData = await response.json();
        console.log("QR用データ" + JSON.stringify(data));

        setContractData(data);

        const allKeys = data.keys.map(
          (key): QrKeyDataWithId => ({
            autoKeyId: key.autoKeyId,
            keyIndexInContract: key.keyIndexInContract,
            vendor: key.sdVender,
            version: key.sdVersion,
            id: key.activateId,
            password: key.password,
            eep: data.eep,
            doorPlace: key.sdc.doorPlace,
          }),
        );

        let filteredKeys: QrKeyDataWithId[];

        if (autoKeyId === "all") {
          // QR全表示
          filteredKeys = allKeys;
        } else {
          // QR単体表示
          const keyIdNumber = Number(autoKeyId);
          filteredKeys = allKeys.filter((key) => key.autoKeyId === keyIdNumber);
        }

        setKeys(filteredKeys);
      } catch (error) {
        console.error(error);
      }
    };

    // 非同期関数を実行
    fetchData();
  }, [autoBuildingId, autoContractId, autoKeyId]);

  return (
    <>
      <style>
        {`
          @media print {
            body {
              width: 210mm;
              height: 297mm;
              margin: 0;
              padding: 0;
            }
            @page {
              size: A4 portrait;
              margin: 0;
            }
          }
        `}
      </style>
      <Box
        px={5}
        py={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography color="error" fontWeight="bold">印刷時はA4縦を指定してください</Typography>
        <BackButton />
      </Box>
      <ReactToPrint
        trigger={() => (
          <Button sx={{ marginLeft: 5 }} variant="contained" color="primary" size="large">
            契約情報・QRコードPDF保存/印刷
          </Button>
        )}
        content={() => qrCodesRef.current}
        documentTitle={
          autoKeyId === "all"
            ? `鍵シート_${contractData?.building.buildingId}-${contractData?.contractId}.pdf`
            : `鍵シート_${contractData?.building.buildingId}-${contractData?.contractId}-${contractData?.keys[0]?.sdc.sdcSerialNumber}-${keys[0]?.keyIndexInContract}.pdf`
        }
      />
      <Box ref={qrCodesRef} height="100%" maxWidth="210mm" margin="0 auto">
        {keys.map((key, index) => (
          <Box
            key={index}
            sx={{ 
              pageBreakAfter: "always",
              padding: "5mm",
              height: "287mm", // A4の高さ（297mm）からマージン（上下5mm x 2）を引いた値
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column"
            }}
          >
            <Paper elevation={0} sx={{ height: "100%", padding: 2 }}>
              {/* ヘッダー部分 */}
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight="bold">
                  デジタル鍵・インターホン用シート
                </Typography>
                <Typography variant="caption">
                  発行日：{currentDateTime}
                </Typography>
              </Box>
              
              {/* 物件情報（大きく表示するが少しコンパクトに） */}
              {contractData && (
                <Box mt={1} mb={1} p={1} bgcolor="#f5f5f5" borderRadius={1} sx={{ pb: 0 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        物件名称
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {contractData.building.buildingName}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        部屋番号
                      </Typography>
                      <Typography variant="h5" fontWeight="bold">
                        {contractData.roomNumber}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* 注意書き（簡潔に） */}
              <Box mb={3}>
                <Typography variant="caption" color="error" fontWeight="bold">
                  ※このシートには大切な情報が含まれています。紛失されることのないよう契約書同様に大切に保管してください。
                  万が一紛失、またはQRコードが第三者に漏れた場合は鍵の悪用を防ぐため速やかに管理会社までお知らせください。
                </Typography>
              </Box>

              {/* アプリの説明（よりコンパクトに） */}
              <Box mb={4}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center">
                      <Box 
                        sx={{ 
                          overflow: 'hidden',
                          width: 40, 
                          height: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <img src={iconFtSmartkey} alt="FTスマートキー" style={{ height: "30px" }} />
                      </Box>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ ml: 1 }}>FTスマートキー</Typography>
                    </Box>
                    <Typography variant="caption">
                      このアプリでQRコードを読み取ると、デジタルキーを取得できます。
                      使用者欄には、使用する人の名前を記入してください。
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center">
                      <Box 
                        sx={{ 
                          overflow: 'hidden',
                          width: 40, 
                          height: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <img src={iconFamiliaCall} alt="Familia Call" style={{ height: "30px" }} />
                      </Box>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ ml: 1 }}>Familia Call</Typography>
                    </Box>
                    <Typography variant="caption">
                      このアプリから同じQRコードを読み取ることで、インターホン用アカウントを取得できます。
                    </Typography>
                  </Grid>
                </Grid>
                {/* このテキストはQRコードの下に移動します */}
              </Box>

              {/* QRコードカード（より効率的にスペース活用） */}
              <Box display="flex" justifyContent="center">
                <Card elevation={0} sx={{ width: "100%", maxWidth: "450px", p: 1, border: '1px solid #ddd', borderRadius: '8px' }}>
                  <CardContent sx={{ p: 1, pb: 1, "&:last-child": { pb: 1 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" fontWeight="bold">
                        鍵No：{key.keyIndexInContract}
                      </Typography>
                      <Typography variant="body2">
                        対象ドア：{key.doorPlace}
                      </Typography>
                    </Box>

                    <Box display="flex" justifyContent="center" my={1}>
                      {(() => {
                        // QRCodeのvalue用のオブジェクトを動的に生成
                        const baseQrValueObj = {
                          vendor: key.vendor,
                          version: key.version,
                          id: key.id,
                          password: key.password,
                        };

                        // eepが空文字やundefinedでなければ、baseQrValueObjとマージ
                        const qrValueObj =
                          key.eep && key.eep.trim() !== ""
                            ? { ...baseQrValueObj, eep: key.eep }
                            : baseQrValueObj;
                        return (
                          <QRCode
                            value={JSON.stringify(qrValueObj)}
                            size={180}
                            renderAs="svg"
                            includeMargin={true}
                          />
                        );
                      })()}
                    </Box>

                    <Typography variant="body2" gutterBottom fontWeight="bold">
                      ID：{key.id}
                    </Typography>

                    <Box mt={2} p={1}>
                      <Typography variant="body2" gutterBottom>
                        鍵使用者： ______________________________
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        （使用者記入欄）
                      </Typography>
                    </Box>
                    
 
                  </CardContent>
                </Card>
              </Box>
              <Box my={3}>
                <Typography variant="caption" fontSize="0.8rem" display="block" textAlign="left">
                  スマートフォンを失くした場合や機種変更時は、このQRコードを新しいスマートフォンで読み取ることで再取得できます。
                  前のデジタルキーとインターホンアカウントは無効化され、セキュリティが確保されます。
                </Typography>
              </Box>
              {/* システムID情報（より小さく表示） */}
              {contractData && (
                <Box mt="auto" pt={1}>
                  <Typography variant="caption" color="text.secondary" fontSize="0.7rem" display="block">
                    契約システムID：{contractData.contractId}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontSize="0.7rem" display="block">
                    物件システムID：{contractData.building.buildingId}
                  </Typography>
                </Box>
              )}

              {/* フッタ―（ロゴ） */}
              {/* <Box 
                display="flex" 
                justifyContent="flex-end" 
                alignItems="center"
                mt={1}
              >
                <img
                  src={fulltechLogo}
                  alt="fulltechLogo"
                  style={{
                    height: "20px",
                    marginLeft: "10px",
                  }}
                />
                <img
                  src={haudiLogo}
                  alt="haudiLogo"
                  style={{
                    height: "20px",
                    marginLeft: "10px",
                  }}
                />
              </Box> */}
            </Paper>
          </Box>
        ))}
      </Box>
    </>
  );
};

export default MasterKeyQrPage;
